import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { POSITION_COLORS, POSITION_BG, MATURITY_BG } from "@/lib/constants";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { HelpCircle, Tags, Search, X, Info, GitCompareArrows, CheckCircle2, FlaskConical, Target, Globe2, Users } from "lucide-react";

const SALIENCE_BG = {
  High: "bg-teal-50 text-teal-700 border-teal-200",
  Moderate: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Low: "bg-slate-50 text-slate-600 border-slate-200",
};

const IND_TIPS = {
  "Strategic salience": "How important this theme is within the current cross-sectional assessment — a composite of clinical relevance, frequency across assessed material, HCP-voice diversity, evidence implications and potential influence on treatment adoption.",
  "Evidence confidence": "How well the theme's synthesis is anchored in identifiable, quality-assessed evidence and convergent HCP input.",
  "HCP-voice diversity": "Breadth of specialties, segments, settings and geographies contributing statements to this theme.",
};

const TAX_GROUPS = ["All groups", "Patient Identification and Testing", "Treatment Selection and Sequencing", "Efficacy and Clinical Outcomes", "Safety, Tolerability and Patient Management", "Evidence, Access and Competitive Context"];

const Indicator = ({ label, value }) => (
  <div className="flex-1 min-w-[110px] bg-slate-50 border border-slate-200/80 rounded-lg px-3 py-2" title={IND_TIPS[label]}>
    <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 flex items-center gap-1">{label}<Info className="w-2.5 h-2.5" /></p>
    <p className={`text-xs font-bold mt-0.5 ${value === "High" ? "text-teal-700" : value === "Moderate" ? "text-indigo-700" : "text-slate-600"}`}>{value || "Insufficient evidence"}</p>
  </div>
);

export default function Themes({ onExploreTheme, onExploreTaxonomy }) {
  const [themes, setThemes] = useState([]);
  const [taxonomy, setTaxonomy] = useState([]);
  const [taxSearch, setTaxSearch] = useState("");
  const [taxGroup, setTaxGroup] = useState("All groups");
  const [selectedTax, setSelectedTax] = useState(null);
  const [sortBy, setSortBy] = useState("salience");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/themes").then((r) => setThemes(r.data.themes));
    api.get("/taxonomy").then((r) => setTaxonomy(r.data.taxonomy));
  }, []);

  const activeTax = taxonomy.filter((t) => t.status === "active"
    && (taxGroup === "All groups" || t.group === taxGroup)
    && t.name.toLowerCase().includes(taxSearch.toLowerCase()));

  const SAL = { High: 0, Moderate: 1, Low: 2 };
  const CONF = { High: 0, Moderate: 1, Low: 2 };
  const visibleThemes = useMemo(() => {
    let list = themes;
    if (selectedTax) list = list.filter((t) => (selectedTax.theme_ids || []).includes(t.id));
    const sorters = {
      salience: (a, b) => (SAL[a.strategic_salience] ?? 3) - (SAL[b.strategic_salience] ?? 3),
      confidence: (a, b) => (CONF[a.evidence_confidence] ?? 3) - (CONF[b.evidence_confidence] ?? 3),
      alpha: (a, b) => a.name.localeCompare(b.name),
    };
    return [...list].sort(sorters[sortBy]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themes, selectedTax, sortBy]);

  const openDetail = (themeId) => {
    api.get(`/themes/${themeId}/analysis`).then((r) => setDetail(r.data));
  };

  return (
    <div className="space-y-6" data-testid="themes-view">
      <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="taxonomy-grid">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Tags className="w-4 h-4 text-teal-600" />
          <h3 className="font-heading text-base font-bold text-slate-900">Clinical-Theme Taxonomy</h3>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-slate-400">{taxonomy.filter((t) => t.status === "active").length} active themes · managed in Admin & Review</span>
        </div>
        <p className="text-xs text-slate-500 mb-3">The classification backbone of this assessment. Click a theme to filter the syntheses below to where it appears.</p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative w-52">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input data-testid="taxonomy-search" placeholder="Search themes…" value={taxSearch} onChange={(e) => setTaxSearch(e.target.value)}
              className="h-8 pl-8 text-xs rounded-full bg-slate-50 border-slate-200" />
          </div>
          <Select value={taxGroup} onValueChange={setTaxGroup}>
            <SelectTrigger data-testid="taxonomy-group-filter" className="w-auto h-8 text-xs gap-1.5 rounded-full border-slate-200 bg-slate-50">
              {taxGroup}
            </SelectTrigger>
            <SelectContent>
              {TAX_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          {selectedTax && (
            <button data-testid="taxonomy-clear" onClick={() => setSelectedTax(null)}
              className="flex items-center gap-1 h-8 px-3 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full hover:bg-red-100">
              <X className="w-3 h-3" /> Clear: {selectedTax.name}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {activeTax.map((t) => {
            const sel = selectedTax?.id === t.id;
            return (
              <button
                key={t.id}
                data-testid={`taxonomy-chip-${t.id}`}
                onClick={() => setSelectedTax(sel ? null : t)}
                title={t.description}
                className={`group flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                  sel ? "bg-slate-900 text-teal-300 border-slate-900" : "text-slate-700 bg-slate-50 border-slate-200 hover:border-teal-400 hover:bg-teal-50/50"}`}
              >
                {t.name}
                <span className={`font-mono text-[10px] ${sel ? "text-teal-400" : "text-slate-400 group-hover:text-teal-600"}`}>{t.insight_ids.length}</span>
              </button>
            );
          })}
          {activeTax.length === 0 && <p className="text-xs text-slate-400">No themes match.</p>}
        </div>
        {selectedTax && (
          <p className="text-[11px] text-slate-500 mt-2.5" data-testid="taxonomy-selection-note">
            <span className="font-semibold text-slate-700">{selectedTax.name}</span> · {selectedTax.insight_ids.length} linked statements ·{" "}
            <button onClick={() => onExploreTaxonomy(selectedTax.id)} className="text-teal-700 font-semibold hover:text-teal-900" data-testid="taxonomy-open-feed">open statements in HCP Voice →</button>
          </p>
        )}
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="font-heading text-base font-bold text-slate-900">Analytical Theme Syntheses <span className="font-mono text-xs font-normal text-slate-400 ml-1">{visibleThemes.length} of {themes.length}</span></h3>
            <p className="text-[11px] text-slate-400 mt-0.5">Single cross-sectional assessment — longitudinal trajectory will become available after multiple assessment periods.</p>
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="theme-sort" className="w-auto h-8 text-xs gap-1.5 rounded-full border-slate-200 bg-white">
              <span className="text-slate-400">Sort:</span>
              {sortBy === "salience" ? "Strategic salience" : sortBy === "confidence" ? "Evidence confidence" : "Alphabetical"}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="salience">Strategic salience</SelectItem>
              <SelectItem value="confidence">Evidence confidence</SelectItem>
              <SelectItem value="alpha">Alphabetical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {visibleThemes.map((t) => {
            const mix = Object.entries(t.position_mix || {}).filter(([, v]) => v > 0);
            const total = mix.reduce((a, [, v]) => a + v, 0) || 1;
            const vol = t.volume || {};
            return (
              <div key={t.id} data-testid={`theme-card-${t.id}`} className="bg-white border border-slate-200/80 rounded-xl p-6 shadow-sm shadow-slate-100 hover:border-teal-400 transition-colors flex flex-col">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <Badge variant="outline" className={`text-[10px] font-semibold ${MATURITY_BG[t.maturity]}`}>{t.maturity}</Badge>
                  <Badge variant="outline" className={`text-[10px] font-semibold ${SALIENCE_BG[t.strategic_salience]}`} title={t.salience_rationale}>Strategic salience: {t.strategic_salience}</Badge>
                </div>
                <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400">Primary clinical theme</p>
                <h3 className="font-heading text-base font-bold text-slate-900 leading-snug mt-0.5">{t.name}</h3>
                <p className="text-[13px] text-slate-600 leading-relaxed mt-2 line-clamp-3">{t.description}</p>

                <div className="flex items-start gap-2 mt-3 bg-amber-50/50 border border-amber-100 rounded-lg px-3 py-2">
                  <GitCompareArrows className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-700 leading-snug"><span className="font-semibold">Key tension:</span> {t.key_tension}</p>
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  <Indicator label="Strategic salience" value={t.strategic_salience} />
                  <Indicator label="Evidence confidence" value={t.evidence_confidence} />
                  <Indicator label="HCP-voice diversity" value={t.voice_diversity} />
                </div>

                <div className="mt-4">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1" data-testid={`theme-volume-${t.id}`}>
                    {vol.statements} relevant statements · {vol.hcp_voices} HCP voices · {vol.sources} sources · {vol.countries} countries <span className="text-amber-600/70 normal-case">· demonstration data</span>
                  </p>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">Position distribution · {t.verified_insight_count ?? t.insight_count} verified curated statements</p>
                  {t.governance_note && <p className="text-[9px] text-amber-700 mb-1" data-testid={`theme-governance-note-${t.id}`}>{t.governance_note}</p>}
                  <div className="flex h-2.5 rounded-full overflow-hidden">
                    {mix.map(([k, v]) => (
                      <div key={k} style={{ width: `${(v / total) * 100}%`, backgroundColor: POSITION_COLORS[k] }} title={`${k}: ${v} (${Math.round((v / total) * 100)}%)`} />
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                    {mix.map(([k, v]) => (
                      <span key={k} className="text-[10px] text-slate-500 flex items-center gap-1" title={`${k}: ${v} statements (${Math.round((v / total) * 100)}%)`}>
                        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: POSITION_COLORS[k] }} />{k} ({v})
                      </span>
                    ))}
                  </div>
                </div>

                {t.key_questions?.[0] && (
                  <p className="flex items-start gap-1.5 text-xs text-slate-600 mt-3">
                    <HelpCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" /><span><span className="font-semibold text-slate-700">Priority question:</span> {t.key_questions[0]}</span>
                  </p>
                )}

                <div className="flex items-center gap-4 mt-auto pt-4">
                  <button
                    data-testid={`theme-detail-${t.id}`}
                    onClick={() => openDetail(t.id)}
                    className="text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-full px-4 py-1.5 transition-colors"
                  >
                    View theme details
                  </button>
                  <button
                    data-testid={`theme-explore-${t.id}`}
                    onClick={() => onExploreTheme(t.id)}
                    className="text-xs font-semibold text-teal-700 hover:text-teal-900 transition-colors"
                  >
                    {t.insight_count} linked insights →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {visibleThemes.length === 0 && <p className="text-sm text-slate-400 py-10 text-center" data-testid="themes-empty">No theme syntheses are linked to this taxonomy theme yet — open its statements in HCP Voice instead.</p>}
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="theme-detail-drawer">
          {detail && (
            <>
              <SheetHeader>
                <div className="flex flex-wrap gap-1.5 mb-1">
                  <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Illustrative prototype</Badge>
                  <Badge variant="outline" className={`text-[10px] ${MATURITY_BG[detail.maturity]}`}>{detail.maturity}</Badge>
                  <Badge variant="outline" className={`text-[10px] ${SALIENCE_BG[detail.strategic_salience]}`}>Salience: {detail.strategic_salience}</Badge>
                  <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">Confidence {detail.analysis.confidence}/100</Badge>
                </div>
                <SheetTitle className="font-heading text-lg leading-snug text-left">{detail.name}</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Full theme synthesis</p>
                  <p className="text-sm text-slate-700 leading-relaxed border-l-2 border-teal-500 pl-3">{detail.analysis.position_synthesis}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Why this theme matters</p>
                  <p className="text-xs text-slate-600 leading-snug">{detail.description}</p>
                </div>
                <div className="bg-emerald-50/50 border border-emerald-100 rounded-lg p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-700 mb-1.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Supporting evidence & positions</p>
                  <ul className="space-y-1">{detail.analysis.supporting_evidence.map((e) => <li key={e} className="text-xs text-slate-700">• {e}</li>)}</ul>
                </div>
                <div className="bg-red-50/40 border border-red-100 rounded-lg p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-red-700 mb-1.5 flex items-center gap-1"><FlaskConical className="w-3 h-3" />Contradictory or challenging positions</p>
                  <ul className="space-y-1">{detail.analysis.contradictory_evidence.map((e) => <li key={e} className="text-xs text-slate-700">• {e}</li>)}</ul>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Representative HCP statements</p>
                  <div className="space-y-2">
                    {detail.representative_insights.map((r) => (
                      <div key={r.id} className="bg-slate-50 border border-slate-200/80 rounded-lg p-3">
                        <p className="text-[11px] text-slate-700 italic leading-snug">"{r.quote}"</p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-[10px] text-slate-500 truncate">{r.attribution?.hcp_name || r.hcp_role} · {r.country} · <span className="font-mono">{r.id}</span></p>
                          <Badge variant="outline" className={`text-[9px] shrink-0 ${POSITION_BG[r.position]}`}>{r.position}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><Users className="w-3 h-3" />Segment differences</p>
                    <p className="text-xs text-slate-600 leading-snug">{detail.analysis.segment_differences}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><Globe2 className="w-3 h-3" />Strategic interpretation</p>
                    <p className="text-xs text-slate-700 font-medium leading-snug">{detail.analysis.strategic_interpretation}</p>
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Unanswered clinical questions</p>
                  <ul className="space-y-1">{detail.key_questions.map((q) => <li key={q} className="text-xs text-slate-600 flex items-start gap-1.5"><HelpCircle className="w-3 h-3 text-amber-500 shrink-0 mt-0.5" />{q}</li>)}</ul>
                </div>
                <div className="bg-teal-50/60 border border-teal-100 rounded-lg p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-teal-700 mb-1.5 flex items-center gap-1"><Target className="w-3 h-3" />Recommended actions</p>
                  <ul className="space-y-1">{detail.analysis.recommended_actions.map((a) => <li key={a} className="text-xs text-slate-700">• {a}</li>)}</ul>
                </div>
                <button
                  data-testid="drawer-open-insights"
                  onClick={() => { onExploreTheme(detail.id); setDetail(null); }}
                  className="text-xs font-semibold text-teal-700 hover:text-teal-900"
                >
                  Open all linked source statements in HCP Voice →
                </button>
                <p className="text-[10px] text-slate-400">Scientific review status: in review · All content is illustrative demonstration data — source verification required before external use.</p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
